import React, { useEffect } from 'react';
import { Card, Typography, Row, Col, Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { ShoppingCartOutlined, StarOutlined, HomeOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const Houston = () => {
  const history = useHistory();

  useEffect(() => {
    const localSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Comfort Bears in Houston - Lumi Bears",
      "description": "Premium comfort companions for Houston families. Emotional support bears designed to help children in Houston with transitions and emotional regulation.",
      "url": "https://lumibears.com/locations/houston", 
      "about": {
        "@type": "Product",
        "name": "Lumi Comfort Bears",
        "description": "Emotional support companions for children"
      },
      "audience": {
        "@type": "Audience",
        "geographicArea": {
          "@type": "City",
          "name": "Houston", 
          "containedInPlace": {
            "@type": "State",
            "name": "Texas"
          }
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(localSchema);
    document.head.appendChild(script);

    document.title = "Comfort Bears Houston - Emotional Support for Houston Children | Lumi Bears";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium comfort bears for Houston families. Help Houston children with transitions and emotional support. Fast shipping to Heights, Memorial, Galleria.');
    }

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent.includes('houston')) {
        existingScript.remove();
      }
      document.title = "Lumi Bears - Comfort Companions for Sensitive Children | Emotional Support Teddy Bears";
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Premium comfort companions for children who need extra emotional support. Soft, intelligent teddy bears designed to help kids feel calm and secure during bedtime and challenging moments.');
      }
    };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Hero Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #8e24aa 0%, #6a1b9a 100%)',
        padding: '60px 0',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Row gutter={[48, 32]} align="middle">
            <Col xs={24} lg={14}>
              <Title level={1} style={{ color: 'white', marginBottom: '20px', fontSize: '2.5rem' }}>
                Comfort Bears in Houston
              </Title>
              <Paragraph style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', marginBottom: '30px' }}>
                Supporting Houston families with premium comfort companions designed for children who need 
                extra emotional support. From the Heights to Sugar Land, Memorial to Clear Lake - 
                helping your child thrive in Space City.
              </Paragraph>
              <Button 
                type="primary" 
                size="large" 
                icon={<ShoppingCartOutlined />}
                onClick={() => history.push('/')}
                style={{ 
                  background: 'white', 
                  borderColor: 'white', 
                  color: '#8e24aa',
                  height: '50px',
                  fontSize: '16px'
                }}
              >
                Shop Comfort Bears
              </Button>
            </Col>
            <Col xs={24} lg={10}>
              <div style={{ textAlign: 'center' }}>
                <StarOutlined style={{ fontSize: '120px', color: 'rgba(255,255,255,0.3)' }} />
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 20px' }}>
        
        {/* Why Houston Families Choose Lumi Bears */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={16}>
            <Title level={2} style={{ color: '#333', marginBottom: '24px' }}>
              Why Houston Families Trust Lumi Bears
            </Title>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.7', color: '#666', marginBottom: '20px' }}>
              Houston's rapid growth, diverse communities, and unique blend of southern hospitality and urban energy 
              create distinctive experiences for families. From navigating excellent schools to embracing cultural diversity, 
              Houston parents need comfort solutions that help children stay grounded while exploring all the opportunities this great city offers.
            </Paragraph>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.7', color: '#666' }}>
              Whether you're in established neighborhoods like River Oaks, growing communities like Katy, or anywhere 
              in between, our comfort bears provide consistent emotional support that helps Houston children build 
              confidence and resilience in this dynamic Texas metropolis.
            </Paragraph>
          </Col>
          <Col xs={24} lg={8}>
            <Card style={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <HomeOutlined style={{ fontSize: '48px', color: '#8e24aa', marginBottom: '16px' }} />
              <Title level={4} style={{ color: '#333' }}>Texas Strong</Title>
              <Text style={{ color: '#666' }}>
                Built with Texas resilience for Houston families who value both independence and emotional support.
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Shipping & Delivery Info */}
        <Card style={{ marginBottom: '40px', background: '#f3e5f5' }}>
          <Row gutter={[32, 24]} align="middle">
            <Col xs={24} md={4} style={{ textAlign: 'center' }}>
              <ClockCircleOutlined style={{ fontSize: '48px', color: '#8e24aa' }} />
            </Col>
            <Col xs={24} md={20}>
              <Title level={3} style={{ color: '#333', marginBottom: '16px' }}>
                Fast Delivery to Greater Houston Area
              </Title>
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Inner Loop:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Greater Houston:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Suburbs:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
              </Row>
              <Paragraph style={{ marginTop: '16px', color: '#666', marginBottom: 0 }}>
                <Text italic>
                  All orders ship with tracking. Current delivery time reflects our pre-order production schedule 
                  as we carefully craft each comfort companion for Texas families.
                </Text>
              </Paragraph>
            </Col>
          </Row>
        </Card>

        {/* Houston-Specific Challenges */}
        <Title level={2} style={{ color: '#333', marginBottom: '30px', textAlign: 'center' }}>
          Supporting Houston Children Through City Growth
        </Title>
        
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Rapid Change & Growth</Title>
              <Paragraph style={{ color: '#666' }}>
                Houston's constant development means frequent changes in neighborhoods, schools, and social environments. 
                Comfort bears provide stability and emotional support during times of transition and growth.
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Cultural Diversity</Title>
              <Paragraph style={{ color: '#666' }}>
                Houston's incredible diversity brings amazing opportunities and complex social navigation for children. 
                Comfort bears offer consistent emotional support as kids learn to appreciate and navigate different cultures.
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Academic Excellence</Title>
              <Paragraph style={{ color: '#666' }}>
                Houston's competitive academic environment and numerous opportunities can create pressure for children. 
                Comfort bears provide emotional regulation support for school stress and achievement expectations.
              </Paragraph>
            </Card>
          </Col>
        </Row>

        {/* Family Resources for Houston */}
        <Card style={{ marginBottom: '40px' }}>
          <Title level={3} style={{ color: '#333', marginBottom: '24px' }}>
            Resources for Houston Families
          </Title>
          <Row gutter={[32, 24]}>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#8e24aa' }}>Child Development Support</Title>
              <ul style={{ color: '#666', lineHeight: '1.6' }}>
                <li>
                  <a href="https://www.texaschildrens.org/departments/psychiatry-and-behavioral-sciences" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#8e24aa' }}>
                    Texas Children's Hospital - Behavioral Health
                  </a>
                </li>
                <li>
                  <a href="https://www.houstonisd.org/mentalhealth" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#8e24aa' }}>
                    Houston ISD - Mental Health Resources
                  </a>
                </li>
              </ul>
            </Col>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#8e24aa' }}>Family Activities & Wellness</Title>
              <ul style={{ color: '#666', lineHeight: '1.6' }}>
                <li>
                  <a href="https://www.houstonparks.org/programs" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#8e24aa' }}>
                    Houston Parks & Recreation - Family Programs
                  </a>
                </li>
                <li>
                  <a href="https://houstonlibrary.org/kids-teens" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#8e24aa' }}>
                    Houston Public Library - Children's Programs
                  </a>
                </li>
              </ul>
            </Col>
          </Row>
        </Card>

        {/* Areas We Serve */}
        <Card style={{ marginBottom: '40px', background: '#f5f5f5' }}>
          <Title level={3} style={{ color: '#333', marginBottom: '24px' }}>
            Serving Houston Areas & Communities
          </Title>
          <Row gutter={[24, 16]}>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#8e24aa' }}>Inner Loop:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Heights</li>
                <li>Montrose</li>
                <li>Midtown</li>
                <li>Museum District</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#8e24aa' }}>West Houston:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Memorial</li>
                <li>Galleria</li>
                <li>River Oaks</li>
                <li>West University</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#8e24aa' }}>North & Northeast:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Spring</li>
                <li>The Woodlands</li>
                <li>Humble</li>
                <li>Kingwood</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#8e24aa' }}>Southwest:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Sugar Land</li>
                <li>Katy</li>
                <li>Bellaire</li>
                <li>Pearland</li>
              </ul>
            </Col>
          </Row>
        </Card>

        {/* Call to Action */}
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
          borderRadius: '12px'
        }}>
          <Title level={2} style={{ color: '#4a148c', marginBottom: '20px' }}>
            Ready to Support Your Houston Child?
          </Title>
          <Paragraph style={{ fontSize: '18px', color: '#6a1b9a', marginBottom: '30px', maxWidth: '600px', margin: '0 auto 30px auto' }}>
            Join Houston families who are giving their children the emotional support tools they need to thrive 
            in this diverse, dynamic Texas metropolis.
          </Paragraph>
          <Button 
            type="primary" 
            size="large" 
            icon={<ShoppingCartOutlined />}
            onClick={() => history.push('/')}
            style={{ 
              background: '#4a148c', 
              borderColor: '#4a148c',
              height: '50px',
              fontSize: '18px',
              padding: '0 30px'
            }}
          >
            Shop Comfort Bears
          </Button>
        </div>

      </div>
    </div>
  );
};

export default Houston;